import { Account } from '../../../../server/services/accounts/account.contractparts';
import { AccountsGetRequest } from '../../../../server/services/accounts/get.contract';
import {
  AcademicEducation,
  AcademicEducationInternals,
  Address,
  Application,
  ApplicationComment,
  ApplicationError,
  SchoolEducation,
  StudyField
} from '../../../../server/services/application/application.contractpart';
import { ApplicationCommentAddRequest } from '../../../../server/services/application/comment/add.contract';
import { ApplicationCreateRequest } from '../../../../server/services/application/create.contract';
import { ApplicationFieldChangeRequest } from '../../../../server/services/application/field/change.contract';
import { ApplicationGetRequest } from '../../../../server/services/application/get.contract';
import { CoreFile } from '../../../../server/services/core/files/files.contractparts';
import { LanguageProficiencyProofs } from "../../../../server/services/language/english.static";
import { Country } from "../../../../server/services/masterData/countries/countries.contractpart";
import { MasterDataCountriesQueryRequest } from '../../../../server/services/masterData/countries/query.contract';
import { StudyProgramsGetRequest } from '../../../../server/services/studyPrograms/get.contract';
import { StudyProgram } from '../../../../server/services/studyPrograms/study-programs.contractpart';
import { Dictionary } from '../../../../shared/d';
import { ExtendedPromise } from '../../../../shared/extended-promise';
import { $a, $ab, $abd, $ac, $ae, $ag, $ap, $aw, sortBy } from '../../../../shared/helper';
import { getValue, isEmpty, isNil, isOneOf, setValue } from '../../../../shared/o';
import { ApplicationFieldChangeEvent } from '../../events/application/field/change.event';
import { ApplicationFieldCommentChangeEvent } from '../../events/application/field/comment/change.event';
import { Session } from '../../global/session';
import { globalState } from '../../global/state';
import { ViewManager } from '../../global/view-manager';

// special country IDs - will be updated when country list is loaded, because id may be different on different systems
export let GERMANY_COUNTRY_ID = '67bcd1a7713f5785c810e2a1';
export let PAKISTAN_COUNTRY_ID = '67ce8a7f378637c4bfba2b42';

export class CurrentApplication {
  private static _data: Application;
  private static _applicant: Account;
  private static changeFieldTimeout: any;
  private static changeFieldRequests: Dictionary<ValueChangePair> = {};

  static countries: Country[] = []
  static studyProgram?: StudyProgram;
  static unlockedFields: Set<string> = new Set<string>();
  static initializedPromise?: ExtendedPromise = new ExtendedPromise();

  static get data(): Application {
    return this._data;
  }

  static set data(value: Application) {
    this._data = value;
  }

  static get applicant(): Account {
    return this._applicant;
  }

  static async create() {
    this.data = await new ApplicationCreateRequest().execute();
    if (!globalState.applications.some(a => a.id = this.data.id)) {
      globalState.applications = [...globalState.applications, this.data];
    }
    await this.initData();
    if (Session.account.creationDate < new Date(2025, 3, 5)) {
      await ViewManager.alert('before you start please notice, that if you have already completed your studies at a German university, you can apply directly via ' +
        'the <a href="https://campus.tu-chemnitz.de/qisserver/pages/cs/sys/portal/hisinoneStartPage.faces?page=Bewerbungsportal">university portal</a> ');
    }
  }

  static async load(application: string | Application) {
    if (application && typeof application === 'string') {
      this._data = await new ApplicationGetRequest({ applicationId: application }).execute();
    } else {
      this._data = application as Application
    }
    await this.initData();
  }

  static async changeField<T = any>(fieldName: string, newValue: T, oldValue?: T): Promise<void> {
    const propertyName = fieldName.replace(/\s/g, '.');
    oldValue ??= getValue(propertyName, this.data);
    setValue(propertyName, newValue, this.data);
    let storedValue: any;
    try {
      storedValue = await new ApplicationFieldChangeRequest({
        applicationId: this.data.id,
        fieldName: fieldName,
        value: newValue instanceof CoreFile ? undefined : newValue,
        file: newValue instanceof CoreFile ? newValue : undefined,
      }).execute();
      if (JSON.stringify(storedValue ?? '') !== JSON.stringify(newValue ?? '')) {
        setValue(propertyName, storedValue, this.data);
      }
    } catch (e) {
      console.error(e);
      return;
    }
    // revalidate this field
    if (!isOneOf(fieldName, 'errors', 'currentStep')) {
      this.validate(fieldName);
    }
    // change comment-state
    if (CurrentApplication.data.fieldComments?.[fieldName]?.state === 'support-comment') {
      CurrentApplication.data.fieldComments[fieldName].state = 'applicant-comment';
      new ApplicationFieldCommentChangeEvent({ fieldName }).emit();
    }
    // emit change event
    new ApplicationFieldChangeEvent({ fieldName, oldValue, newValue, storedValue }).emit();
  }

  static async changeFieldDelayed<T = any>(fieldName: string, newValue: T) {
    const propertyName = fieldName.replace(/\s/g, '.');
    clearTimeout(this.changeFieldTimeout);
    if (this.changeFieldRequests.hasOwnProperty(fieldName)) {
      this.changeFieldRequests[fieldName].newValue = newValue;
    } else {
      this.changeFieldRequests[fieldName] = {
        oldValue: getValue(propertyName, this.data),
        newValue: newValue,
      };
    }
    setValue(propertyName, newValue, this.data);
    this.changeFieldTimeout = setTimeout(async () => {
      const changes = this.changeFieldRequests;
      this.changeFieldRequests = {};
      for (const pair of Object.entries(changes)) {
        await this.changeField(pair[0], pair[1].newValue, pair[1].oldValue);
      }
    }, 1000);
  }

  static reset() {
    this.data = undefined;
  }


  static addError(text: string, step: number, field?: keyof Application | string) {
    this.data.errors ??= [];
    if (this.data.errors.some(e => e.text === text && e.field === field)) {
      return;
    }
    this.data.errors.push(new ApplicationError(text, step, field));
  }

  private static async initData() {
    this.countries = await new MasterDataCountriesQueryRequest().execute();
    GERMANY_COUNTRY_ID = this.countries.find(c => c.iso2 === 'DE')?.id;
    PAKISTAN_COUNTRY_ID = this.countries.find(c => c.iso2 === 'PK')?.id;
    sortBy(this.countries, c => c.nameEN);
    this.data.currentStep ??= 1;
    this.data.state ??= 'new';
    this.data.files ??= {};
    this.data.correspondenceAddress ??= new Address();
    this.data.academicBackground ??= [new AcademicEducation({
      id: crypto.randomUUID(),
      internals: new AcademicEducationInternals(),
      degreeInfo: new StudyField()
    })];
    for (const item of this.data.academicBackground) {
      item.id ??= crypto.randomUUID();
    }
    this.data.schoolBackground ??= new SchoolEducation({ id: crypto.randomUUID() });
    if (this.data.schoolBackground && !this.data.schoolBackground.id) {
      this.data.schoolBackground.id = crypto.randomUUID();
    }
    if (Session.account?.hasRole('reviser')) {
      this.data.internals ??= {}
    }
    this.studyProgram = await new StudyProgramsGetRequest({ id: this.data.studyProgrammId }).execute();
    // applicant account
    if (Session.account.hasOneRole('reviser', 'university', 'approver')) {
      this._applicant = await new AccountsGetRequest({ accountId: this._data.accountId }).execute();
    } else if (this._data.accountId === Session.account.id) {
      this._applicant = Session.account;
    }
    this.initializedPromise.resolve();
  }

  static isFieldDisabled(field: string) {
    if (!Session.account?.hasOneRole('reviser', 'applicant')) {
      return true;
    }
    if (this.data.state === 'new') {
      return false;
    }
    if (this.unlockedFields.has(field)) {
      return false;
    }
    if (this.data.errors.some(e => e.field === field)) {
      return false;
    }
    const comment = this.data.fieldComments?.[field];
    if (!comment) {
      return true;
    }
    return comment.state === 'done';
  }

  static async addFieldComment(field: string, message: string): Promise<ApplicationComment> {
    const comment = await new ApplicationCommentAddRequest({
      applicationId: this.data.id,
      field,
      message,
    }).execute();
    new ApplicationFieldCommentChangeEvent({ fieldName: field, message: message }).emit();
    return comment;
  }

  static validate(fieldName?: string, step?: number) {
    const oldErrors = this.data.errors ?? [];
    this.data.errors = [];

    this.validateStepPerson();
    this.validateStepSchool();
    this.validateStepAcademic();
    this.validateExtraEducation();
    this.validateStepFiles();
    this.validateStepWork();
    this.validatePayment();

    let changeEvent: ApplicationFieldCommentChangeEvent;
    if (fieldName) {
      this.data.errors = [
        ...oldErrors.filter(e => e.field !== fieldName,
          ...this.data.errors.filter(e => e.field === fieldName)
        ),
      ];
      changeEvent = new ApplicationFieldCommentChangeEvent({ fieldName: fieldName });
    } else {
      if (step) {
        this.data.errors = [
          ...oldErrors.filter(e => e.step !== step),
          ...this.data.errors.filter(e => e.step === step),
        ];
      }
      changeEvent = new ApplicationFieldCommentChangeEvent();
    }


    if (fieldName !== 'errors' && JSON.stringify(oldErrors ?? '') !== JSON.stringify(this.data.errors)) {
      changeEvent.emit();
      this.changeField('errors', this.data.errors).then();
    }
  }

  private static validateStepPerson() {

    const requiredFields: { key: string; label: string; pattern?: RegExp }[] = [
      { key: $a('salutation'), label: "Salutation" },
      { key: $a('firstname'), label: "First Name", pattern: /^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/ },
      { key: $a('gender'), label: "Gender" },
      { key: $a('birthdate'), label: "Birthdate" },
      { key: $a('birthplace'), label: "Birthplace", pattern: /^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/ },
      { key: $a('nationality'), label: "Nationality" },
      { key: $ac('country'), label: "Country" },
      { key: $ac('city'), label: "City", pattern: /^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/ },
      { key: $ac('postalCode'), label: "Postal Code", pattern: /^[0-9]+$/ },
      { key: $ac('address'), label: "Address" }
    ];

    requiredFields.forEach(({ key, label, pattern }) => {
      const value = getValue(key.replace(/\s/g, '.'), CurrentApplication.data);

      if (isEmpty(value)) {
        this.addError(`${label} is missing`, 1, key);
      } else if (pattern && !pattern.test(value.toString())) {
        this.addError(`${label} contains invalid characters`, 1, key);
      }
    });

    if (!CurrentApplication.data.files["identificationDocumentFront"]) {
      this.addError("Identification document (front) is missing", 1, "files identificationDocumentFront");
    }
    if (!CurrentApplication.data.files["identificationDocumentRear"]) {
      this.addError("Identification document (rear) is missing", 1, "files identificationDocumentRear");
    }
  }


  private static validateStepSchool() {
    const schoolBackground = CurrentApplication.data.schoolBackground;

    if (!schoolBackground.schoolName?.trim()) {
      this.addError("School Name is missing", 2, "schoolBackground schoolName");
    }

    if (!schoolBackground.countryId?.trim()) {
      this.addError("Country is missing", 2, "schoolBackground countryId");
    }

    if (!schoolBackground.city?.trim()) {
      this.addError("City is missing", 2, "schoolBackground city");
    }

    if (!schoolBackground.certificateName?.trim()) {
      this.addError("School Leaving Certificate Name is missing", 2, "schoolBackground certificateName");
    }

    if (isNil(schoolBackground.start?.year)) {
      this.addError(`Start Year is missing or invalid for study school period`, 2, `schoolBackground start`);
    }

    if (isNil(schoolBackground.end?.year)) {
      this.addError(`End Year is missing or invalid for study school period`, 2, `schoolBackground end`);
    }

    if (isNil(schoolBackground.start?.month)) {
      this.addError(`Start Month is missing or invalid for study school period`, 2, `schoolBackground start`);
    }

    if (isNil(schoolBackground.end?.month)) {
      this.addError(`End Month is missing or invalid for school period`, 2, `schoolBackground end`);
    }

    if (!CurrentApplication.data.files["school-certificate"]) {
      this.addError("School Certificate file is required", 2, "files school-certificate");
    }
  }

  private static validateStepAcademic() {
    const academicBackground = CurrentApplication.data.academicBackground;

    if (!academicBackground || academicBackground.length === 0) {
      this.addError("At least one academic record is required", 3, "academicBackground");
    }

    academicBackground.forEach((university, index) => {
      if (isEmpty(university.kindOfStudy)) {
        this.addError(`Kind of study is missing for study ${index + 1}`, 3, $ab(index, 'kindOfStudy'));
      }

      if (isNil(university.graduatingUniversity)) {
        this.addError(`title giving university decision missing for study ${index + 1}`, 3, $ab(index, 'graduatingUniversity'));
      }
      if (!university.countryId?.trim()) {
        this.addError(`Country is missing for study ${index + 1}`, 3, $ab(index, 'countryId'));
      } else {
        if (CurrentApplication.isApsCountry(university.countryId) && university.graduatingUniversity) {
          if (!CurrentApplication.data.files[`aps-certificate-${index + 1}`]) {
            this.addError(`APS Document is required for study ${index + 1}`, 3, `files aps-certificate-${index + 1}`);
          }
        } else {
          if (CurrentApplication.data.files[`aps-certificate-${index + 1}`]) {
            CurrentApplication.changeField(`files aps-certificate-${index + 1}`, undefined).then();
          }
        }
      }

      if (!university.city?.trim()) {
        this.addError(`City is missing for study ${index + 1}`, 3, $ab(index, 'city'));
      }

      if (isEmpty(university.institution)) {
        this.addError(`University Name is missing for study ${index + 1}`, 3, $ab(index, 'institution'));
      }

      if (!university.degreeInfo?.degreeName?.trim()) {
        this.addError(`Degree Name is missing for study ${index + 1}`, 3, $abd(index, 'degreeName'));
      }

      if (!university.degreeInfo?.programName?.trim()) {
        this.addError(`Program Name is missing for study ${index + 1}`, 3, $abd(index, 'programName'));
      }

      if (isNil(university.startDate?.year)) {
        this.addError(`Start Year is missing or invalid for study ${index + 1}`, 3, $ab(index, 'startDate'));
      }

      if (isNil(university.endDate?.year)) {
        this.addError(`End Year is missing or invalid for study ${index + 1}`, 3, $ab(index, 'endDate'));
      }

      if (isNil(university.startDate?.month)) {
        this.addError(`Start Month is missing or invalid for study ${index + 1}`, 3, $ab(index, 'startDate'));
      }

      if (isNil(university.endDate?.month)) {
        this.addError(`End Month is missing or invalid for study ${index + 1}`, 3, $ab(index, 'endDate'));
      }

      if (university.countryId === GERMANY_COUNTRY_ID) {
        if (university.isGraduated) {
          this.addError(`You have already completed your studies at a German university, so you must apply directly via the <a
            href="https://campus.tu-chemnitz.de/qisserver/pages/cs/sys/portal/hisinoneStartPage.faces?page=Bewerbungsportal">university
            portal</a>.`, 3, $ab(index, 'isGraduated'));
        } else if (isNil(university.numberOfSemesters)) {
          this.addError(`For studies in germany the number of studied semesters is necessary (study ${index + 1})`, 3, $ab(index, 'numberOfSemesters'));
        }
      }

      if (!university.GPA?.toString().trim()) {
        this.addError(`GPA is missing for study ${index + 1}`, 3, $ab(index, 'GPA'));
      }

      // fields for validation of certificates of pakistan
      if (university.countryId === PAKISTAN_COUNTRY_ID) {
        if (university.isGraduated === false) {
          this.addError(
            `For the selected country, an application with a provisional degree is not possible because a CNIC plus attestation stamp number are required but provisional degree documents does not have the stamp. (study ${index + 1})`,
            3,
            $ab(index, 'isGraduated')
          );
        }
        if (isEmpty(university.cnic)) {
          this.addError(
            `For the selected country, CNIC is required (study ${index + 1})`,
            3,
            $ab(index, 'cnic')
          );
        }
        if (isEmpty(university.stampNumber)) {
          this.addError(
            `For the selected country, Stamp Number is required (study ${index + 1})`,
            3,
            $ab(index, 'stampNumber')
          );
        }
      }

      if (isNil(university.isGraduated)) {
        this.addError(`Have you already completed study ${index + 1} ?`, 3, $ab(index, 'isGraduated'));
      } else if (university.isGraduated) {
        if (!CurrentApplication.data.files[`academic-grades-${index + 1}`]) {
          this.addError(`Transcript of Records is required for study ${index + 1}`, 3, `files academic-grades-${index + 1}`);
        }
        if (!CurrentApplication.data.files[`academic-certificate-${index + 1}`]) {
          this.addError(`Certificate is required for study ${index + 1}`, 3, `files academic-certificate-${index + 1}`);
        }
      } else {
        if (!university.creditsLeft?.trim()) {
          this.addError(`Provide with how much credits lefts for study ${index + 1}`, 3, $ab(index, 'creditsLeft'));
        }
        if (!CurrentApplication.data.files[`academic-grades-${index + 1}`]) {
          this.addError(`Current Transcript of Records is required for study ${index + 1}`, 3, `files academic-grades-${index + 1}`);
        }
      }

      if (!CurrentApplication.data.files[`grading-system-${index + 1}`]) {
        this.addError(`Grading System Description document is required for study ${index + 1}`, 3, `files grading-system-${index + 1}`);
      }

      if (university.kindOfStudy === 'master' && university.countryId === GERMANY_COUNTRY_ID && isNil(university.examsFinallyFailed)) {
        this.addError(`The info if you finally failed some exams for study ${index + 1} is missing`, 3, $ab(index, 'examsFinallyFailed'));
      }
      if (university.examsFinallyFailed && !university.failedModuleTitle?.trim()) {
        this.addError(`Title of failed module for study ${index + 1} is missing`, 3, $ab(index, 'failedModuleTitle'));
      }
    });

    if (
      CurrentApplication.data.academicBackground?.every(
        (university) => isNil(university.graduatingUniversity)
      ) ?? true
    ) {
      this.addError(`Provide at least one university you have graduated or plan on graduating from`, 3, `academicBackground`);
    }
  }

  private static validateExtraEducation() {

    if (CurrentApplication.data.preparatoryCollege && Object('')) {
      if (isEmpty(CurrentApplication.data.preparatoryCollege?.institution)) {
        this.addError(`Institution Name is missing from Preparatory College Details`, 4, `preparatoryCollege institution`);
      }
      if (!CurrentApplication.data.preparatoryCollege?.city?.trim()) {
        this.addError(`City is missing from Preparatory College Details`, 4, `preparatoryCollege city`);
      }
      if (!CurrentApplication.data.preparatoryCollege?.postalCode?.trim()) {
        this.addError(`Postal Code is missing from Preparatory College Details`, 4, `preparatoryCollege postalCode`);
      } else {
        if (!/^\d+$/.test(CurrentApplication.data.preparatoryCollege?.postalCode?.trim())) {
          this.addError(`Postal code is invalid in Preparatory College Details`, 4, `preparatoryCollege postalCode`);
        }
      }
      if (!CurrentApplication.data.files["preparatory-proof"] && CurrentApplication.data.preparatoryCollege.assessmentTestPassed) {
        this.addError(`Preparatory College proof document is missing from Preparatory College Details`, 4, `files preparatory-proof`);
      }
      if (isEmpty(CurrentApplication.data.preparatoryCollege?.graduationDate)) {
        this.addError(`Graduation Date is missing from Preparatory College Details`, 4, $ap('graduationDate'));
      }
    } else {
      for (let i = CurrentApplication.data.errors.length - 1; i >= 0; i--) {
        if (CurrentApplication.data.errors[i].field?.startsWith("preparatoryCollege") || CurrentApplication.data.errors[i].field?.startsWith("files preparatory-proof")) {
          CurrentApplication.data.errors.splice(i, 1);
        }
      }
    }

    if (CurrentApplication.data.entranceExam?.isCompleted) {
      if (!CurrentApplication.data.entranceExam?.name?.trim()) {
        this.addError(`Exam Name is missing from Entrance Exam Details`, 4, `entranceExam name`);
      }
      if (!CurrentApplication.data.entranceExam?.date?.toString().trim()) {
        this.addError(`Exam Date is missing from Entrance Exam Details`, 4, `entranceExam date`);
      }
    } else {
      for (let i = CurrentApplication.data.errors.length - 1; i >= 0; i--) {
        if (CurrentApplication.data.errors[i].field?.startsWith("entranceExam") || CurrentApplication.data.errors[i].field?.startsWith("files entranceExam")) {
          CurrentApplication.data.errors.splice(i, 1);
        }
      }
    }
    if (isNil(CurrentApplication.data.germanGrade?.hasKnowledge)) {
      this.addError(`Do you have any knowledge of German? (Official Certificate)`, 4, $ag('hasKnowledge'));
    }
    if (CurrentApplication.data.germanGrade?.hasKnowledge) {
      const germanGrade = CurrentApplication.data.germanGrade
      if (!germanGrade.name) {
        this.addError("Type of German Certificate is missing", 4, $ag('name'));
      }

      if (germanGrade.name) {
        if (!germanGrade.level?.trim()) {
          this.addError("Level of German is missing", 4, $ag("level"));
        }

        if (isEmpty(germanGrade.institution)) {
          this.addError("Issuing Institution Name is missing", 4, $ag("institution"));
        }

        if (!germanGrade.location?.trim()) {
          this.addError("Institution Location is missing", 4, $ag("location"));
        }

        if (!germanGrade.issueDate) {
          this.addError("Issue Date is missing", 4, $ag("issueDate"));
        } else {
          const issueDate = new Date(germanGrade.issueDate.year ?? 0, germanGrade.issueDate.month ?? 0, germanGrade.issueDate.day ?? 0);
          const today = new Date();

          if (issueDate > today) {
            this.addError("Issue Date cannot be in the future", 4, $ag("issueDate"));
          }
        }

        if (!CurrentApplication.data.files["german-certificate"]) {
          this.addError("Proof of German Certificate is required", 4, "files german-certificate");
        }
        if (isEmpty(CurrentApplication.data.germanCertificateId)) {
          this.addError("ID of the German Certificate is required", 4, $a('germanCertificateId'));
        }
      }
    }

    if (!CurrentApplication.data.englishGrade?.hasKnowledge) {
      this.addError(`Proof of English knowledge is required for application to this program`, 4, $ae(`hasKnowledge`));
    }
    if (CurrentApplication.data.englishGrade?.hasKnowledge) {
      if (!CurrentApplication.data.englishGrade?.name) {
        this.addError(`Type of English proof is missing`, 4, $ae('name'));
      } else {
        if (!CurrentApplication.data.files['english-proof']) {
          this.addError(`Upload English language certificate/proof in languages section`, 4, `files english-proof`);
        }
        const englishProofType = LanguageProficiencyProofs.getAllProofs().find(c => c.name == CurrentApplication.data.englishGrade.name)
        if (englishProofType?.requiresRlwsGrade) {
          if (!CurrentApplication.data.englishGrade?.scoreSections?.trim()) {
            this.addError(`English language scores in different sections is missing in languages section`, 4, $ae('scoreSections'));
          }
        }
        if (englishProofType?.requiresInput) {
          if (!CurrentApplication.data.englishGrade?.level?.trim()) {
            this.addError(`English Level is missing in languages section`, 4, $ae('level'));
          }
        }
      }
    }
  }

  private static validateStepFiles() {
    (CurrentApplication.data.additionalFiles ?? [])?.forEach((file, index) => {
      if (!file) {
        return;
      }
      if (file.file && (!file.category?.trim())) {
        this.addError(`Category is missing from ${file.file.fileName}`, 6, `additionalFiles ${index} category`);
      }
      if (file.file && file.category === 'other' && (!file.description?.trim())) {
        this.addError(`description is missing from ${file.file.fileName}`, 6, `additionalFiles ${index} description`);
      }

      if (!file.file && ((file?.description?.trim().length > 0) || (file?.category?.trim().length > 0))) {
        this.addError(`file is missing in step upload`, 6, `additionalFiles ${index} file`);
      }
    })
  }

  private static validateStepWork() {

    CurrentApplication.data.workBackground?.forEach((job, index) => {
      if (!job) {
        return;
      }
      if (job.from?.year && job.to?.year && job.from.year > job.to.year) {
        this.addError(`End Year cannot be earlier than start Year for job ${index + 1}`, 5, $aw(index, 'to'));
      }
    });
  }

  private static validatePayment(): void {
    if (
      !CurrentApplication.data.payment?.bankTransferAuthorized && (
        !CurrentApplication.data.payment?.paypalOrderId ||
        !CurrentApplication.data.payment?.paymentDate
      )
    ) {
      this.addError('outstanding payment', 7, 'payment');
    }
  }

  static isApsCountry(countryId: string): boolean {
    const location = this.countries.find(c => c.id === countryId);
    return (location && isOneOf(location?.iso2, "CN", "IN", "VN"));
  }
}


interface ValueChangePair {
  oldValue: any;
  newValue: any;
}
